/*
 * Which rows of a list are ticked for one action on all of them: a box clicked, Shift+click for the
 * run between it and the last box clicked, Shift+J/K or Shift+Arrow ticking as the cursor moves, and
 * Cmd/Ctrl+A for every row shown (docs/2026-09-30-inbox-bulk-selection-shape.md). Pure, so the inbox,
 * Mailboxes and their tests share one rule. Rows are named by id, never by position: a list that
 * refreshes under the rep moves positions, and an id still says which row was meant.
 *
 * The old inbox's `src/bulk-selection.ts` held the same rules and left with that inbox (e8029b3a7);
 * this is that module without its whole-tab selection, which needs a server action the inbox no
 * longer has.
 */
export type Ticks = {
  readonly ids: ReadonlySet<string>;
  /** The row a Shift+click ranges from: the last one clicked, ticked by a key, or ranged to. */
  readonly anchor: string | null;
  /**
   * A run Shift+J/K or Shift+Arrow is extending: its fixed end, the row the cursor has reached, and
   * the rows ticked before it began. Anything else that ticks ends it.
   */
  readonly run: {
    readonly from: string;
    readonly at: string;
    readonly base: ReadonlySet<string>;
  } | null;
};

export const NO_TICKS: Ticks = { ids: new Set(), anchor: null, run: null };

/** The rows from position `a` to position `b`, both included, in the list's order. */
function between(order: readonly string[], a: number, b: number): readonly string[] {
  return a <= b ? order.slice(a, b + 1) : order.slice(b, a + 1);
}

/** A box clicked, or X: that row flips, and the next Shift+click ranges from it. */
export function tickOne(ticks: Ticks, id: string): Ticks {
  const ids = new Set(ticks.ids);
  if (!ids.delete(id)) ids.add(id);
  return { ids, anchor: id, run: null };
}

/**
 * Shift+click on `id`: every row from the anchor to it takes the state the clicked row is given, as
 * Gmail and Finder do, so a run ticked by mistake is unticked the same way it was ticked. Before any
 * row has been clicked the run starts at `cursor` (the row the keyboard is on, or the open one), as
 * Finder's starts at the focused item. With neither among `order` it is a plain click.
 */
export function tickRange(
  ticks: Ticks,
  id: string,
  order: readonly string[],
  cursor: string | null = null,
): Ticks {
  const anchor = ticks.anchor ?? cursor;
  const from = anchor === null ? -1 : order.indexOf(anchor);
  const to = order.indexOf(id);
  if (from === -1 || to === -1) return tickOne(ticks, id);
  const on = !ticks.ids.has(id);
  const ids = new Set(ticks.ids);
  for (const row of between(order, from, to)) {
    if (on) ids.add(row);
    else ids.delete(row);
  }
  return { ids, anchor: id, run: null };
}

/**
 * Shift+J/K or Shift+Arrow, the cursor moving from `from` to `to`. The first press starts a run at
 * `from`; every press ticks the rows from the run's start to where the cursor now is, over what was
 * ticked before the run began. So walking back shrinks the run, as Finder, Linear and the WAI-ARIA
 * listbox do, and an overshoot is taken back with the opposite key; it never unticks a row the run
 * did not tick. A `from` not in the list (nothing was under the cursor) starts the run at `to`.
 */
export function tickThrough(
  ticks: Ticks,
  from: string,
  to: string,
  order: readonly string[],
): Ticks {
  const end = order.indexOf(to);
  if (end === -1) return ticks;
  const going = ticks.run?.at === from && order.includes(ticks.run.from) ? ticks.run : null;
  const run = going ?? { from: order.includes(from) ? from : to, at: from, base: ticks.ids };
  const ids = new Set(run.base);
  for (const row of between(order, order.indexOf(run.from), end)) ids.add(row);
  return { ids, anchor: run.from, run: { ...run, at: to } };
}

/** Cmd/Ctrl+A or the bar's box: every row shown. Pressed again it keeps them; it never unticks. */
export const tickAll = (ticks: Ticks, order: readonly string[]): Ticks => ({
  ids: new Set([...ticks.ids, ...order]),
  anchor: ticks.anchor,
  run: null,
});

export const allTicked = (ticks: Ticks, order: readonly string[]): boolean =>
  order.length > 0 && order.every((id) => ticks.ids.has(id));

/**
 * The ticks a new list still shows. A row that left it (a search narrowed it, a refresh took it away,
 * someone else marked it done) leaves the selection, so the count is always what an action will
 * touch. The same `ticks` comes back when nothing left, so a caller can tell nothing changed.
 */
export function keepShown(ticks: Ticks, order: readonly string[]): Ticks {
  const shown = new Set(order);
  const anchorGone = ticks.anchor !== null && !shown.has(ticks.anchor);
  const kept = [...ticks.ids].filter((id) => shown.has(id));
  if (kept.length === ticks.ids.size && !anchorGone) return ticks;
  return { ids: new Set(kept), anchor: anchorGone ? null : ticks.anchor, run: null };
}
